<template>
  <Teleport to="body">
    <transition name="slide-fade">
      <div v-if="isOpen" class="side-settings-modal" :style="modalStyle">
        <div class="side-settings-header" @mousedown="startDrag" :style="{ cursor: isDragging ? 'grabbing' : 'grab' }">
          <div class="side-settings-title-group">
            <span class="material-symbols-outlined side-settings-icon">settings</span>
            <div class="side-settings-headline">
              <h3>Semester settings</h3>
              <div class="side-settings-subtitle">{{ currentSemesterName }}</div>
            </div>
          </div>
          <span class="material-symbols-outlined close-btn" @click="$emit('close')" title="Close Settings">close</span>
        </div>
        
        <div class="side-settings-body">
          <!-- Workload preferences -->
          <div class="side-settings-section">
            <div class="side-settings-section-header">
              <span class="material-symbols-outlined section-icon">work_history</span>
              <span>Workload limits</span>
            </div>
            <p class="section-desc">Adjust bounds for ECTS and exams in this semester.</p>
            
            <div class="side-settings-grid">
              <div class="side-settings-item">
                <label>Min. ECTS</label>
                <input v-if="localSemesterPreferences && !localSemesterPreferences.default" v-model.number="localSemesterPreferences.minEcts" max="60" min="0" type="number" class="side-settings-input">
                <span v-else class="side-settings-value-disabled">{{ preferences.minEctsPerSemester }} (Global)</span>
              </div>
              <div class="side-settings-item">
                <label>Max. ECTS</label>
                <input v-if="localSemesterPreferences && !localSemesterPreferences.default" v-model.number="localSemesterPreferences.maxEcts" max="60" min="0" type="number" class="side-settings-input">
                <span v-else class="side-settings-value-disabled">{{ preferences.maxEctsPerSemester }} (Global)</span>
              </div>
              <div class="side-settings-item">
                <label>Min. exams</label>
                <input v-if="localSemesterPreferences && !localSemesterPreferences.default" v-model.number="localSemesterPreferences.minExams" max="10" min="0" type="number" class="side-settings-input">
                <span v-else class="side-settings-value-disabled">{{ preferences.minExamsPerSemester }} (Global)</span>
              </div>
              <div class="side-settings-item">
                <label>Max. exams</label>
                <input v-if="localSemesterPreferences && !localSemesterPreferences.default" v-model.number="localSemesterPreferences.maxExams" max="10" min="0" type="number" class="side-settings-input">
                <span v-else class="side-settings-value-disabled">{{ preferences.maxExamsPerSemester }} (Global)</span>
              </div>
            </div>
            
            <div class="side-settings-actions">
              <button class="side-settings-action-btn" @click.stop="toggleLocalDefaultSettings">
                {{ localSemesterPreferences && localSemesterPreferences.default ? "Customize settings" : "Reset to global" }}
              </button>
            </div>
          </div>

          <!-- Free days preferences 
          <div v-if="!preferences.ignoreTimetable" class="side-settings-section">
            <div class="side-settings-section-header">
              <span class="material-symbols-outlined section-icon">calendar_today</span>
              <span>Free Days</span>
            </div>
            <p class="section-desc">Choose days on which no classes should be planned:</p>
            <div class="side-free-days-grid">
              <label v-for="day in ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']" :key="day" class="side-free-day-checkbox-label">
                <input type="checkbox" :checked="localSemesterPreferences && localSemesterPreferences['free' + day.substring(0,3)]" @change="toggleLocalFreeDay(day)" />
                <span class="checkbox-custom"></span>
                <span class="day-name">{{ day.substring(0, 3) }}</span>
              </label>
            </div>
          </div> -->
        </div>

        <div class="side-settings-footer">
          <button 
            class="btn-confirm-settings" 
            @click="applySettings"
            :disabled="!changeDetected"
            title="Apply settings and save changes"
          >
            Apply settings
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { mapState } from 'vuex';
import { currentSemester as getCurrentSemester, formatSemester , getAbsoluteSemester} from "@/utils";

export default {
  name: "SemesterSettingsModal",
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    semester: {
      type: Number,
      default: null
    }
  },
  emits: ['close', 'apply'],
  data() {
    return {
      localSemesterPreferences: {},
      isDragging: false,
      position: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 }
    };
  },
  computed: {
    ...mapState(["preferences"]),
    currentSemester() {
      return this.semester !== null ? this.semester : getCurrentSemester(this.preferences);
    },
    currentSemesterName() {
      const semesterCode = getAbsoluteSemester(this.currentSemester, this.preferences.startSemester);
      const val=formatSemester(semesterCode)

      return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    },
    changeDetected() {
      if (!this.localSemesterPreferences) return false;
      const storePrefs = this.preferences.semesterPreferences && this.preferences.semesterPreferences[this.currentSemester] 
          ? this.preferences.semesterPreferences[this.currentSemester] 
          : { default: false };
          
      for (const key of Object.keys(this.localSemesterPreferences)) {
          if (this.localSemesterPreferences[key] !== storePrefs[key]) {
              return true;
          }
      }
      return false;
    },
    modalStyle() {
      return {
        transform: `translate(${this.position.x}px, ${this.position.y}px)`
      };
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.initPreferences();
        this.position = { x: 0, y: 0 }; // Reset position on open
      }
    },
    currentSemester() {
      this.initPreferences();
    }
  },
  created() {
    this.initPreferences();
  },
  methods: {
    initPreferences() {
      if (this.preferences && this.preferences.semesterPreferences && this.preferences.semesterPreferences[this.currentSemester]) {
        this.localSemesterPreferences = JSON.parse(JSON.stringify(this.preferences.semesterPreferences[this.currentSemester]));
      } else {
        this.localSemesterPreferences = {};
      }
    },
    toggleLocalDefaultSettings() {
      if (!this.localSemesterPreferences) {
        this.localSemesterPreferences = { default: false };
      }
      if (this.localSemesterPreferences.default) {
        this.localSemesterPreferences.default = false;
        this.localSemesterPreferences.minEcts = this.preferences.minEctsPerSemester;
        this.localSemesterPreferences.maxEcts = this.preferences.maxEctsPerSemester;
        this.localSemesterPreferences.minExams = this.preferences.minExamsPerSemester;
        this.localSemesterPreferences.maxExams = this.preferences.maxExamsPerSemester;
      } else {
        this.localSemesterPreferences.default = true;
        this.localSemesterPreferences.minEcts = this.preferences.minEctsPerSemester;
        this.localSemesterPreferences.maxEcts = this.preferences.maxEctsPerSemester;
        this.localSemesterPreferences.minExams = this.preferences.minExamsPerSemester;
        this.localSemesterPreferences.maxExams = this.preferences.maxExamsPerSemester;
      }
    },
    toggleLocalFreeDay(day) {
      if (!this.localSemesterPreferences) {
        this.localSemesterPreferences = { default: false };
      }
      const key = 'free' + day.substring(0, 3);
      this.localSemesterPreferences = {
        ...this.localSemesterPreferences,
        [key]: !this.localSemesterPreferences[key]
      };
    },
    applySettings() {
      if (this.localSemesterPreferences) {
        Object.keys(this.localSemesterPreferences).forEach(key => {
            this.$store.commit("UPDATE_SEMESTER_PREFERENCE", { 
                semester: this.currentSemester, 
                key: key, 
                value: this.localSemesterPreferences[key],
                bulk: true
            });
        });
      }
      this.$store.commit("SAVE_STATE");
      this.$store.dispatch("requestSchedule");
      this.$emit('apply');
      this.$emit('close');
    },
    // Movable modal dragging methods
    startDrag(event) {
      if (event.target.closest('.close-btn') || event.target.closest('input') || event.target.closest('button')) {
        return; 
      }
      this.isDragging = true;
      this.dragOffset.x = event.clientX - this.position.x;
      this.dragOffset.y = event.clientY - this.position.y;
      
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    },
    onDrag(event) {
      if (!this.isDragging) return;
      this.position.x = event.clientX - this.dragOffset.x;
      this.position.y = event.clientY - this.dragOffset.y;
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    }
  }
};
</script>

<style scoped>
/* Side Settings Modal Styles */
.side-settings-modal {
  position: fixed;
  right: 25px;
  top: 120px;
  width: 360px;
  height: 470px;
  max-height: calc(100vh - 160px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  font-family: inherit;
  user-select: none;
}

.side-settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;

  background-color: #f9fafb;
  border-bottom: 1px solid #f3f4f6;
  user-select: none;
}

.side-settings-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.side-settings-icon {
  font-size: 22px;
  color: #9ABCE4;
}
.side-settings-headline {
  display: flex;
  flex-direction: column;
}
.side-settings-headline h3 {
  font-size: 1.05rem;
  font-weight: 600;
  padding: 0px;
  margin: 0px;
  margin-bottom: -8px;
  color: #1f2937;
}

.side-settings-subtitle {
  font-size: 0.75rem;
  color: #6b7280;
}

.side-settings-header .close-btn {
  font-size: 20px;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s, color 0.2s;
}

.side-settings-header .close-btn:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.side-settings-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: #ffffff;
}

.side-settings-section {
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  padding: 16px;
  background-color: #ffffff;
}

.side-settings-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.side-settings-section-header .section-icon {
  font-size: 18px;
  color: #9ABCE4;
}

.section-desc {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 12px;
  line-height: 1.3;
}

.side-settings-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 15px;
}

.side-settings-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.side-settings-item label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #4b5563;
}

.side-settings-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.85rem;
  width: 100%;
  height: 25px;
  box-sizing: border-box;
  color: #1f2937;
  background: #fff;
  transition: border-color 0.2s;
}

.side-settings-input:focus {
  border-color: #5e94d4;
  outline: none;
}

.side-settings-value-disabled {
  font-size: 0.85rem;
  color: #9ca3af;
  height: 25px;
  box-sizing: border-box;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  display: inline-block;
  text-align: center;
}

.side-settings-action-btn {
  background: none;
  border: 1px solid #5e94d4;
  color: #5e94d4;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  width: 100%;
  text-align: center;
}

.side-settings-action-btn:hover {
  background-color: #5E94D4;
  color: white;
}

.side-free-days-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.side-free-day-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  font-size: 0.85rem;
  color: #374151;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: #fff;
  transition: border-color 0.2s, background-color 0.2s;
}

.side-free-day-checkbox-label:hover {
  border-color: #5e94d4;
  background-color: #f9fafb;
}

.side-free-day-checkbox-label input {
  display: none;
}

.side-free-day-checkbox-label input:checked + .checkbox-custom {
  background-color: #5e94d4;
  border-color: #5e94d4;
}

.side-free-day-checkbox-label input:checked + .checkbox-custom::after {
  content: "";
  display: block;
  width: 3px;
  height: 6px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-custom {
  width: 14px;
  height: 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.side-settings-footer {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  background-color: #f9fafb;
  border-top: 1px solid #f3f4f6;
  gap: 10px;
}

.btn-confirm-settings {
 
  background-color: #9ABCE4;
  color: white;
  border: 1px solid #9ABCE4;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-confirm-settings:hover:not(:disabled) {
  background-color: #5E94D4;
}

.btn-confirm-settings:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(120%);
  opacity: 0;
}
</style>
